/**
 * Fair Price live commentary
 *
 * Turns the engine's Fair Price state (anchors, phase, armed gate, signals)
 * into plain-English narration: where we are, what to do right now, and what
 * to watch for next. The chart shows lines; this says what they mean.
 *
 * Pure functions only — no React, no stores, no clock reads. Everything comes
 * in through `CommentaryInput` so the whole thing is unit-testable and the
 * panel stays a dumb renderer.
 *
 * Strategy semantics mirror utt-engine/src/strategy/fair_price.rs exactly:
 *   - session opens (ET): rth 09:30, pm 14:00, evening6pm 18:00, evening8pm 20:00
 *   - 0-10 min after an open  -> "continuation" (trade WITH the opening candle)
 *   - 10-90 min after an open -> "reversion"    (trade back TOWARD fair price)
 *   - otherwise               -> "dead"         (no trade)
 *   - signals fire in the reversion phase only, and only while `armed`
 *     (|price - anchor| >= tpPoints)
 *
 * See docs/strategies/jj-simon-fair-price.md for the source material.
 */

import type {
  FairPriceAnchor,
  FairPriceAnchorKind,
  FairPriceSettings,
  FairPriceSignal,
  FairPriceUpdate,
} from '../store/fairPriceStore';
import type { DisplacementMarker } from '../store/displacementStore';

// ============================================================================
// Types
// ============================================================================

/** Drives colour in the panel. */
export type Tone = 'go' | 'wait' | 'stop' | 'muted';

export interface CommentaryHeadline {
  /** Short verdict word shown large: READY / WAIT / STAND DOWN / OFF. */
  verdict: string;
  /** One sentence saying what to do about it. */
  detail: string;
  tone: Tone;
}

export interface Commentary {
  headline: CommentaryHeadline;
  /** Where we are: anchor, distance, clock. */
  situation: string[];
  /** Concrete triggers to watch for, best grade first. */
  lookFor: string[];
  /** Active guards and cautions. */
  warnings: string[];
  /** Trade template when there's a tradeable direction, else null. */
  plan: string | null;
  /** Progress lines for signals still in play. */
  live: string[];
  /** Rolling outcome line, or null when nothing has resolved. */
  score: string | null;
  /** One-paragraph spoken version for text-to-speech. */
  speech: string;
}

export interface CommentaryInput {
  /** Whether the Fair Price indicator is switched on. */
  enabled: boolean;
  /** Latest engine state; null before the first update arrives. */
  state: FairPriceUpdate | null;
  settings: FairPriceSettings;
  /** Signals already filtered to the chart's symbol. */
  signals: FairPriceSignal[];
  /** Displacement markers already filtered to the chart's symbol. */
  displacements: DisplacementMarker[];
  /** Current price, null when unknown. */
  price: number | null;
  /** Now, ms epoch. */
  now: number;
}

// ============================================================================
// Constants (mirror fair_price.rs)
// ============================================================================

const MS_PER_MIN = 60_000;
const CONTINUATION_END_MIN = 10;
const REVERSION_END_MIN = 90;

/** Session opens as minutes-since-ET-midnight, matching the engine. */
const SESSION_OPENS: { session: string; minute: number; gate: keyof FairPriceSettings }[] = [
  { session: 'rth', minute: 9 * 60 + 30, gate: 'sessionsRth' },
  { session: 'pm', minute: 14 * 60, gate: 'sessionsPm' },
  { session: 'evening6pm', minute: 18 * 60, gate: 'sessionsEvening' },
  { session: 'evening8pm', minute: 20 * 60, gate: 'sessionsEvening' },
];

/**
 * Session and anchor names carry NO clock times.
 *
 * The engine defines every session in ET, but the user reads the chart in
 * their own timezone — a hardcoded "9:30" is simply wrong on a PDT screen.
 * Clock labels are therefore always derived from a real timestamp and
 * formatted locally by `formatLocalTime`, never written into these strings.
 */
const SESSION_NAMES: Record<string, string> = {
  rth: 'the RTH open',
  pm: 'the PM session',
  evening6pm: 'the Globex reopen',
  evening8pm: 'the Asia open',
  none: 'no session',
};

const ANCHOR_NAMES: Record<FairPriceAnchorKind, string> = {
  rth929: 'the RTH opening body',
  open6pm: 'the Globex open',
  open8pm: 'the Asia open',
  open2pm: 'the PM open',
  prenews: 'the pre-news body',
  migrated: 'the migrated fair price',
};

/** How recent a displacement has to be to still count as "just printed". */
const DISPLACEMENT_FRESH_MS = 10 * MS_PER_MIN;

// ============================================================================
// Small helpers
// ============================================================================

const px = (n: number): string => n.toFixed(2);
const pts = (n: number): string => `${n.toFixed(1)} pts`;

/**
 * A timestamp as a clock time in the VIEWER's timezone (so a PDT user sees
 * 6:30 AM for the 9:30 ET open, not 9:30). Returns '' for a missing time so
 * callers can drop the suffix entirely rather than print a bogus clock.
 */
export function formatLocalTime(ms: number | null | undefined): string {
  if (!ms || !Number.isFinite(ms)) return '';
  try {
    return new Date(ms).toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** "the RTH open (6:30 AM)" — clock only when we have a real timestamp. */
function sessionName(session: string | null | undefined, at?: number | null): string {
  const base = !session ? SESSION_NAMES.none : SESSION_NAMES[session] ?? session;
  const clock = formatLocalTime(at);
  return clock ? `${base} (${clock})` : base;
}

function anchorName(kind: FairPriceAnchorKind | null | undefined, at?: number | null): string {
  const base = !kind ? 'fair price' : ANCHOR_NAMES[kind] ?? 'fair price';
  const clock = formatLocalTime(at);
  return clock ? `${base} (${clock})` : base;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/** Minutes-since-midnight and weekday (0=Sun) in America/New_York. */
export function etParts(nowMs: number): { minute: number; weekday: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
  });
  const parts = fmt.formatToParts(new Date(nowMs));
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '0';
  // en-US hour12:false can render midnight as "24"; normalise it.
  const hour = parseInt(get('hour'), 10) % 24;
  const minute = parseInt(get('minute'), 10);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const weekday = Math.max(0, days.indexOf(get('weekday')));
  return { minute: hour * 60 + minute, weekday };
}

/**
 * Minutes until the next enabled session open, and which one.
 *
 * Works in minute deltas rather than absolute timestamps, so it never has to
 * convert an ET wall-clock time back to UTC. That keeps it correct year-round
 * except across the two DST switchovers, where it can be an hour out for one
 * evening — acceptable for a countdown hint.
 *
 * Returns null when no session is enabled at all.
 */
export function nextSessionOpen(
  nowMs: number,
  settings: FairPriceSettings
): { session: string; minutesAway: number } | null {
  const enabled = SESSION_OPENS.filter((o) => settings[o.gate] === true);
  if (enabled.length === 0) return null;

  const { minute, weekday } = etParts(nowMs);
  const SUNDAY_REOPEN = 18 * 60; // futures reopen Sun 6 PM ET
  const FRIDAY_CLOSE = 17 * 60; // and close Fri 5 PM ET

  // Weekend: the next thing that happens is the Sunday evening reopen.
  const weekendClosed =
    weekday === 6 || // Saturday
    (weekday === 5 && minute >= FRIDAY_CLOSE) || // Friday after the close
    (weekday === 0 && minute < SUNDAY_REOPEN); // Sunday before the reopen
  if (weekendClosed) {
    const daysToSunday = weekday === 0 ? 0 : 7 - weekday;
    const minutesAway = daysToSunday * 1440 + SUNDAY_REOPEN - minute;
    // Only meaningful if an evening session is actually enabled.
    if (settings.sessionsEvening) {
      return { session: 'evening6pm', minutesAway };
    }
    // Otherwise fall through to the first enabled open after the reopen.
    const first = enabled.reduce((a, b) => (a.minute <= b.minute ? a : b));
    return {
      session: first.session,
      minutesAway: daysToSunday * 1440 + first.minute - minute + (first.minute < SUNDAY_REOPEN ? 1440 : 0),
    };
  }

  const laterToday = enabled.filter((o) => o.minute > minute).sort((a, b) => a.minute - b.minute);
  if (laterToday.length > 0) {
    return { session: laterToday[0].session, minutesAway: laterToday[0].minute - minute };
  }
  const earliest = enabled.reduce((a, b) => (a.minute <= b.minute ? a : b));
  return { session: earliest.session, minutesAway: 1440 - minute + earliest.minute };
}

/** "1h 12m" / "42m" */
export function humanMinutes(mins: number): string {
  const m = Math.max(0, Math.round(mins));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

/** The anchor the phase logic is currently driving off, if we can identify it. */
function activeAnchor(state: FairPriceUpdate): FairPriceAnchor | null {
  const kind = state.activeAnchorKind;
  if (kind) {
    const match = state.anchors.find((a) => a.kind === kind);
    if (match) return match;
  }
  return state.anchors.find((a) => a.active) ?? null;
}

/** Signals whose lifecycle hasn't resolved yet. */
function liveSignals(signals: FairPriceSignal[]): FairPriceSignal[] {
  return signals.filter((s) => (s.status ?? 'live') === 'live');
}

/** Signals that fired since the current session opened. */
function signalsThisSession(signals: FairPriceSignal[], sessionOpenAt: number): FairPriceSignal[] {
  if (!sessionOpenAt) return [];
  return signals.filter((s) => s.timestamp >= sessionOpenAt);
}

/** The most recent displacement still inside the freshness window. */
function freshDisplacement(
  displacements: DisplacementMarker[],
  now: number
): DisplacementMarker | null {
  let best: DisplacementMarker | null = null;
  for (const d of displacements) {
    if (d.skipped) continue;
    if (now - d.timestamp > DISPLACEMENT_FRESH_MS) continue;
    if (best === null || d.timestamp > best.timestamp) best = d;
  }
  return best;
}

// ============================================================================
// The commentary
// ============================================================================

export function buildFairPriceCommentary(input: CommentaryInput): Commentary {
  const { enabled, state, settings, signals, displacements, price, now } = input;

  const situation: string[] = [];
  const lookFor: string[] = [];
  const warnings: string[] = [];
  const live: string[] = [];
  let plan: string | null = null;

  // --- Off / not ready ----------------------------------------------------
  if (!enabled) {
    return {
      headline: {
        verdict: 'OFF',
        detail: 'Fair Price is switched off. Turn on FP in the action bar to get live commentary.',
        tone: 'muted',
      },
      situation: [],
      lookFor: [],
      warnings: [],
      plan: null,
      live: [],
      score: null,
      speech: 'Fair Price is switched off.',
    };
  }

  if (!state || !state.phase) {
    return {
      headline: {
        verdict: 'WAITING',
        detail: 'Waiting for the first update from the engine.',
        tone: 'muted',
      },
      situation: [],
      lookFor: [],
      warnings: [],
      plan: null,
      live: [],
      score: null,
      speech: 'Waiting for the first update from the engine.',
    };
  }

  const { phase, armed, distance } = state;
  const anchor = activeAnchor(state);
  const anchorLabel = anchorName(state.activeAnchorKind ?? anchor?.kind ?? null, anchor?.setAt);
  const minsIn = phase.sessionOpenAt
    ? Math.floor((now - phase.sessionOpenAt) / MS_PER_MIN)
    : null;

  // Direction that would be "toward fair price" right now.
  let towardAnchor: 'long' | 'short' | null = null;
  if (anchor && price !== null && Number.isFinite(price)) {
    towardAnchor = price < anchor.price ? 'long' : 'short';
  }

  // --- Where we are -------------------------------------------------------
  if (anchor) {
    situation.push(`Fair price is ${anchorLabel} at ${px(anchor.price)}.`);
  } else {
    situation.push('No fair-price anchor is set yet for this session.');
  }

  if (typeof distance === 'number' && Number.isFinite(distance)) {
    const need = settings.tpPoints;
    if (armed) {
      situation.push(
        `Price is ${pts(distance)} away — past the ${pts(need)} minimum, so there's room to trade.`
      );
    } else {
      situation.push(
        `Price is only ${pts(distance)} away and needs ${pts(need)}. Too close to fair price — from here it's random.`
      );
    }
  }

  const shortSession = sessionName(phase.session, phase.sessionOpenAt);
  if (phase.mode === 'continuation' && minsIn !== null) {
    situation.push(
      `${humanMinutes(minsIn)} into ${shortSession} — still the first ${CONTINUATION_END_MIN} minutes.`
    );
  } else if (phase.mode === 'reversion' && minsIn !== null) {
    const left = REVERSION_END_MIN - minsIn;
    situation.push(
      `${humanMinutes(minsIn)} into ${shortSession}, in the reversion window — ${humanMinutes(left)} left.`
    );
  }

  // --- Phase-driven verdict ----------------------------------------------
  let headline: CommentaryHeadline;

  if (phase.mode === 'dead') {
    const next = nextSessionOpen(now, settings);
    if (next) {
      situation.push(
        `Next window is ${sessionName(
          next.session,
          now + next.minutesAway * MS_PER_MIN
        )}, ${humanMinutes(next.minutesAway)} from now.`
      );
    }
    headline = {
      verdict: 'STAND DOWN',
      detail: phase.sessionOpenAt
        ? `${shortSession} is done — its 90-minute window has closed. Nothing to trade until the next open.`
        : 'No session window is open. Nothing to trade right now.',
      tone: 'stop',
    };
    lookFor.push('Nothing yet — wait for the next session open.');
  } else if (phase.mode === 'continuation') {
    headline = {
      verdict: 'WATCH',
      detail: `First ${CONTINUATION_END_MIN} minutes of ${shortSession}. This is the continuation leg — the move AWAY from fair price.`,
      tone: 'wait',
    };
    situation.push(
      'JJ trades this leg in the direction of the opening candle, not back toward the anchor.'
    );
    lookFor.push(
      'The opening candle closing through prior structure, or the next candle breaking and closing beyond it.'
    );
    lookFor.push('A huge wick or a failure to close through = skip it, or wait one candle.');
    warnings.push(
      'The indicator does not fire signals during continuation — it only marks reversions. This leg is a manual call.'
    );
  } else {
    // reversion
    if (!armed) {
      headline = {
        verdict: 'STAND DOWN',
        detail: `Reversion window is open but price is sitting on fair price. Wait for it to travel at least ${pts(
          settings.tpPoints
        )} away.`,
        tone: 'stop',
      };
      lookFor.push(
        `Price to move ${pts(settings.tpPoints)} or more from ${px(anchor?.price ?? 0)} — then the setup arms.`
      );
    } else {
      const dirWord = towardAnchor === 'long' ? 'long' : towardAnchor === 'short' ? 'short' : null;
      const athBlocked = state.athGuardActive === true && towardAnchor === 'short';

      if (athBlocked) {
        headline = {
          verdict: 'WAIT',
          detail:
            'Armed for a short back to fair price, but price is at the all-time high and JJ takes no displacement shorts there.',
          tone: 'wait',
        };
      } else if (dirWord) {
        headline = {
          verdict: 'READY',
          detail: `Armed for a ${dirWord} back toward ${px(anchor?.price ?? 0)}. Wait for the trigger — don't front-run it.`,
          tone: 'go',
        };
      } else {
        headline = {
          verdict: 'READY',
          detail: 'Armed and in the reversion window. Wait for a trigger toward fair price.',
          tone: 'go',
        };
      }

      const towardWord =
        towardAnchor === 'long' ? 'above' : towardAnchor === 'short' ? 'below' : 'beyond';
      lookFor.push(
        `A+ — a candle that CLOSES ${towardWord} the last swing ${
          towardAnchor === 'long' ? 'high' : 'low'
        }. A wick through it does not count.`
      );
      lookFor.push(
        `A — a displacement candle toward fair price: bigger than the one before it, almost no wick, closing ${towardWord} the previous candle.`
      );

      if (!athBlocked && dirWord) {
        const half = settings.bigCandlePts;
        plan =
          `${dirWord.toUpperCase()} toward ${px(anchor?.price ?? 0)} · stop ${pts(
            settings.slPoints
          )} · target ${pts(settings.tpPoints)} · 1:${(settings.tpPoints / Math.max(settings.slPoints, 0.01)).toFixed(1)}` +
          ` — if the opening candle was over ${pts(half)}, halve the size and double the bracket.`;
      }
    }
  }

  // --- A displacement just printed ---------------------------------------
  const disp = freshDisplacement(displacements, now);
  if (disp && phase.mode === 'reversion') {
    const dirMatches =
      towardAnchor === null ||
      (towardAnchor === 'long' && disp.direction === 'bullish') ||
      (towardAnchor === 'short' && disp.direction === 'bearish');
    if (dirMatches) {
      lookFor.unshift(
        `A ${disp.grade} displacement just printed (${disp.rangePoints.toFixed(
          1
        )} pts, ${disp.direction === 'bullish' ? 'up' : 'down'}) — that's the A-grade trigger firing now.`
      );
    } else {
      warnings.push(
        `The last displacement (${disp.direction === 'bullish' ? 'up' : 'down'}) points AWAY from fair price — that's against the reversion, not a trigger.`
      );
    }
  }

  // --- Guards -------------------------------------------------------------
  if (state.athGuardActive === true) {
    warnings.push(
      `ATH guard is on: price is near the all-time high, so shorts are suppressed. JJ takes no displacement shorts at highs.`
    );
  }

  if (phase.session === 'rth' && phase.mode === 'reversion' && minsIn !== null) {
    const left = REVERSION_END_MIN - minsIn;
    if (left <= 15) {
      warnings.push(
        `RTH closes out in ${humanMinutes(left)} at ${formatLocalTime(
          phase.sessionOpenAt + REVERSION_END_MIN * MS_PER_MIN
        )} — JJ's hard stop. Don't open anything new you can't finish.`
      );
    }
  }

  const sessionSignals = signalsThisSession(signals, phase.sessionOpenAt);
  if (sessionSignals.length >= 3) {
    warnings.push(
      `${sessionSignals.length} signals already this session — JJ caps it at 3 a day, 4th only if it's A+.`
    );
  }

  // --- Live signals -------------------------------------------------------
  for (const sig of liveSignals(signals)) {
    const mfe = sig.maxFavorablePts ?? 0;
    const mae = sig.maxAdversePts ?? 0;
    const toGo = Math.max(0, sig.tpPoints - mfe);
    live.push(
      `${sig.grade} ${sig.direction} from ${px(sig.entry)} — best ${pts(mfe)} in front, worst ${pts(
        mae
      )} against, ${pts(toGo)} still to target.`
    );
  }

  // --- Score --------------------------------------------------------------
  const stats = state.stats ?? null;
  const decided = stats ? stats.wins + stats.stops : 0;
  const score =
    stats && decided > 0
      ? `${stats.wins} ${plural(stats.wins, 'win', 'wins')} / ${stats.stops} ${plural(
          stats.stops,
          'stop',
          'stops'
        )} — ${Math.round(stats.winRatePct)}% (breakeven needs ${Math.round(
          (settings.slPoints / Math.max(settings.slPoints + settings.tpPoints, 0.01)) * 100
        )}%)`
      : null;

  // --- Spoken version -----------------------------------------------------
  const speechParts = [`${headline.verdict}.`, headline.detail];
  if (situation.length > 0) speechParts.push(situation[0]);
  if (situation.length > 1) speechParts.push(situation[1]);
  if (lookFor.length > 0) speechParts.push(`Watch for: ${lookFor[0]}`);
  if (warnings.length > 0) speechParts.push(warnings[0]);
  const speech = speechParts.join(' ');

  return { headline, situation, lookFor, warnings, plan, live, score, speech };
}
